import React, { useMemo, useRef, useState } from 'react';
import { Select } from 'antd';

import {
  ADD_QUICK_RECORD_QUESTION,
  ADD_RECORD_SAVE_FAILED,
} from 'constants/messageConstants';
import {
  ADD_QUICK_RECORD_LABELS,
  ADD_QUICK_RECORD_NOUNS,
} from 'constants/uiConstants';
import { useFetchGSpeciesTypes } from 'hooks/useFetchGSpeciesTypes';
import { v4 as uuidv4 } from 'uuid';
import MergeClassNames from 'utils/MergeClassNames';

import { QuickRecordKind } from './addRecord';
import AddRecordRow from './AddRecordRow';
import { QuickRecord, useCreateMQuickRecord } from './useCreateMQuickRecord';

interface QuickAddConfirmProps {
  kind: QuickRecordKind;
  /** The text typed in the search, which becomes the new record's name. */
  searchText: string;
  onCreated: (record: QuickRecord) => void;
}

/**
 * "Add '<text>'" for records that need only a name (timing, target): the row
 * asks for a confirm in place, then saves and hands the record back to be
 * selected. A target can also take a species type, which can be cleared.
 * Cancel sends nothing; a failed save says so inline (OP-2498).
 */
const QuickAddConfirm: React.FC<QuickAddConfirmProps> = ({
  kind,
  searchText,
  onCreated,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [speciesType, setSpeciesType] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // One id per confirm, reused on retry so a resend can't create two records.
  const idRef = useRef(uuidv4());
  const { mutateAsync, isPending, isError, reset } =
    useCreateMQuickRecord(kind);

  const name = searchText.trim();
  const noun = ADD_QUICK_RECORD_NOUNS[kind];
  const isTarget = kind === 'target';

  // Fetched only once a target confirm is open
  const { data: speciesTypes = [] } = useFetchGSpeciesTypes(
    isTarget && isConfirming
  );
  const speciesTypeOptions = useMemo(
    () => speciesTypes.map((type) => ({ value: type.id, label: type.name })),
    [speciesTypes]
  );

  if (!isConfirming) {
    return (
      <AddRecordRow
        searchText={searchText}
        onClick={() => {
          idRef.current = uuidv4();
          setSpeciesType(null);
          setIsConfirming(true);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2 border-y border-gray-bright bg-success-light px-3 py-3">
      <p className="text-sm font-semibold text-charcoal">
        {ADD_QUICK_RECORD_QUESTION(name, noun)}
      </p>
      {isTarget && (
        <div className="flex flex-col gap-1">
          <label
            htmlFor="quick-add-species-type"
            className="text-sm font-semibold text-gray-dark"
          >
            {ADD_QUICK_RECORD_LABELS.speciesType}
          </label>
          <Select
            id="quick-add-species-type"
            className="w-full"
            showSearch
            allowClear
            disabled={isPending}
            optionFilterProp="label"
            placeholder={ADD_QUICK_RECORD_LABELS.selectSpeciesType}
            options={speciesTypeOptions}
            value={speciesType?.id}
            getPopupContainer={() => document.body}
            onChange={(id?: string) => {
              const type = speciesTypes.find((t) => t.id === id);
              setSpeciesType(type ? { id: type.id, name: type.name } : null);
            }}
          />
        </div>
      )}
      {isError && (
        <p role="alert" className="text-xs font-semibold text-danger-dark">
          {ADD_RECORD_SAVE_FAILED}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={isPending}
          className={MergeClassNames(
            'flex-1 rounded-[var(--radius-button)] bg-success-dark px-3 py-1.5 text-sm font-medium text-white',
            isPending && 'cursor-not-allowed opacity-50'
          )}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            // Not mutate()'s onSuccess: once the lists refetch the new record
            // matches the search, this row unmounts, and a callback tied to a
            // mounted component would then never run (it is selected here).
            mutateAsync({ id: idRef.current, name, speciesType }).then(
              onCreated,
              () => undefined // the failure shows inline via isError
            );
          }}
        >
          {isPending
            ? ADD_QUICK_RECORD_LABELS.adding
            : ADD_QUICK_RECORD_LABELS.confirm}
        </button>
        <button
          type="button"
          disabled={isPending}
          className="flex-1 rounded-[var(--radius-button)] border border-gray-medium bg-white px-3 py-1.5 text-sm font-medium text-charcoal"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            reset();
            setIsConfirming(false);
          }}
        >
          {ADD_QUICK_RECORD_LABELS.cancel}
        </button>
      </div>
    </div>
  );
};

export default QuickAddConfirm;
